import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCoins, FaCrown, FaTrophy, FaGift, FaUsers, FaChartBar, FaBars, FaComments, FaTimes, FaBoxOpen, FaSync, FaSyncAlt, FaBomb, FaChartLine, FaDice, FaBell, FaVolumeUp, FaUser, FaLock, FaGamepad, FaCog, FaSignOutAlt, FaHistory, FaGlobeAmericas, FaHeadset, FaBoxes, FaCaretUp, FaCaretDown, FaCaretRight, FaArrowUp, FaBullseye, FaHeart, FaTicketAlt, FaSearch, FaSlidersH, FaChevronDown, FaChevronUp, FaChevronLeft, FaShieldAlt, FaBolt, FaStar } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { gameAPI, apiHelpers } from './api';
import { UserContext } from './App';

const BRAND_PURPLE = '#ffb703';
const THEME_COLOR = '#ffb703';

// Precise Item Definitions
const BIG_GRIN = { id: 101, name: 'Big Grin', price: 1338.06, chance: 1, color: '#eab308', img: '/biggrin.png' };
const FROSTBITE_FACEMASK = { id: 102, name: 'Frostbite Facemask', price: 120.00, chance: 30, color: BRAND_PURPLE, img: '/icemask.png' };
const STAINLESS_FACEMASK = { id: 103, name: 'Stainless Facemask', price: 60.00, chance: 30, color: BRAND_PURPLE, img: '/icemaskk.png' };
const TEMPERED_MP5 = { id: 104, name: 'Tempered Mp5', price: 647.28, chance: 5, color: BRAND_PURPLE, img: '/temperedmp5.png' };
const BLUE_SHIRT = { id: 105, name: 'Blue Shirt', price: 0.11, chance: 40, color: '#3b82f6', img: '/blueshirt.webp' };
const SCRAP = { id: 106, name: 'Scrap', price: 0.01, chance: 99, color: '#94a3b8', img: '/scrap.webp' };

// Wild West Item Definitions
const JAWBOY = { id: 201, name: 'Jawboy', price: 33.74, chance: 0.1, color: '#ef4444', img: '/jawboy.png' };
const PHANTOM_REVOLVER = { id: 202, name: 'Phantom Revolver', price: 33.55, chance: 0.1, color: '#ef4444', img: '/PhantomRevolver.png' };
const MOROCCO_PYTHON = { id: 203, name: 'Morocco Python', price: 19.54, chance: 0.13, color: '#a855f7', img: '/morrocopython.png' };
const SLAUGHTER = { id: 204, name: 'Slaughter', price: 17.82, chance: 0.15, color: '#a855f7', img: '/slaughter.png' };
const SHERIFF_HAT = { id: 205, name: 'Cowboy Sheriff Hat', price: 8.77, chance: 0.3, color: '#3b82f6', img: '/cowboy.png' };
const SHERIFF_SUIT = { id: 206, name: 'Cowboy Sheriff Suit', price: 8.01, chance: 0.3, color: '#3b82f6', img: '/cowboy2.png' };
const HUNTED_HAT = { id: 207, name: 'Hunted Hat', price: 3.77, chance: 0.71, color: '#22c55e', img: '/haunted.png' };
const COWBOY_HAT = { id: 208, name: 'Cowboy Hat', price: 3.03, chance: 0.59, color: '#22c55e', img: '/hat.png' };
const BATTLE_STEEL = { id: 209, name: 'Hot Battle Steel', price: 2.84, chance: 0.31, color: '#22c55e', img: '/hotbattle.png' };
const COWBOY_SHIRT = { id: 210, name: 'Cowboy Shirt', price: 1.74, chance: 0.51, color: '#3b82f6', img: '/cowboy3.png' };
const COWBOY_PANTS = { id: 211, name: 'Cowboy Pants', price: 1.53, chance: 2.35, color: '#3b82f6', img: '/cowboy4.png' };
const BURN_PANTS = { id: 212, name: 'Chemical Burn Pants', price: 1.21, chance: 2.23, color: '#3b82f6', img: '/chemical.png' };
const GOLDEN_FANG = { id: 213, name: 'Golden Fang Bandana', price: 0.76, chance: 1.18, color: '#94a3b8', img: '/goldenfang.png' };
const SNOW_CAMO = { id: 214, name: 'Snow Camo Bandana', price: 0.41, chance: 2.21, color: '#94a3b8', img: '/snowcamo.png' };
const FOREST_CAMO = { id: 215, name: 'Forest Camo Bandana', price: 0.25, chance: 28.48, color: '#94a3b8', img: '/forestcamo.png' };
const CLOTH = { id: 216, name: 'Cloth', price: 0.14, chance: 31.35, color: '#94a3b8', img: '/cloth.png' };
const EGG_RED = { id: 401, name: 'Red Festive Egg', price: 34.78, chance: 1, color: '#ef4444', img: '/redegg.png' };
const EGG_BLUE = { id: 402, name: 'Blue Festive Egg', price: 78.80, chance: 5, color: '#ffb703', img: '/eggblue.png' };
const ALIEN_RELIC = { id: 403, name: 'Alien Relic', price: 2366.71, chance: 20, color: '#eab308', img: '/alienrelic.png' };
const KNOCK_KNOCK = { id: 504, name: 'Knock Knock', price: 22827.53, chance: 0.1, color: '#eab308', img: '/knockknock.png' };
const TREE_DOOR = { id: 505, name: 'Metal Tree Door', price: 4950.00, chance: 0.5, color: '#eab308', img: '/metaltreedoor.png' };
const TEMPERED_MASK = { id: 506, name: 'Tempered Mask', price: 262.87, chance: 10, color: '#a855f7', img: '/temperedmask.png' };
const GOLD_ROCK = { id: 507, name: 'Gold Rock', price: 143.89, chance: 30, color: '#ffb703', img: '/goldrock.png' };
const LEGENDARY_GOLD = { id: 508, name: 'Legendary Gold Mask', price: 111.87, chance: 59.4, color: '#ffb703', img: '/legendarygoldfacemask.png' };
const GOLDEN_DRAGON = { id: 509, name: 'Golden Dragon', price: 5.18, chance: 10, color: '#3b82f6', img: '/goldendragon.png' };
const THUNDERGOLD_CHEST = { id: 510, name: 'Thundergold Chest', price: 2.45, chance: 30, color: '#22c55e', img: '/thundergoldplate.png' };
const CELESTIAL_EGG = { id: 511, name: 'Celestial Egg', price: 2.95, chance: 20, color: '#22c55e', img: '/whiteegg.webp' };
const PUNISHMENT_MASK = { id: 701, name: 'Punishment Mask', price: 2390.00, chance: 1, color: '#eab308', img: '/punishmentmask.png' };
const CREEPY_CLOWN = { id: 702, name: 'Creepy Clown Bandana', price: 500.00, chance: 10, color: BRAND_PURPLE, img: '/creepyclown.png' };

// Abyss Item Definitions
const RAVEN = { id: 801, name: 'Raven', price: 39.57, chance: 0.02, color: '#ef4444', img: '/raven.png' };
const DARK_ARTS_ICEPICK = { id: 802, name: 'Dark Arts Icepick', price: 26.79, chance: 0.1, color: '#ef4444', img: '/deathpick.png' };
const SACRIFICIAL_MASK_ABYSS = { id: 803, name: 'Sacrificial Mask', price: 19.51, chance: 0.13, color: '#a855f7', img: '/SacrificialMask.png' };
const ABYSS_SLEEPING_BAG = { id: 804, name: 'Abyss Sleeping Bag', price: 6.02, chance: 0.4, color: '#3b82f6', img: '/AbyssSleepingbag.png' };
const ABYSS_THOMPSON = { id: 805, name: 'Abyss Thompson', price: 3.06, chance: 1.15, color: '#22c55e', img: '/AbyssThompson.png' };
const ABYSS_FACEMASK = { id: 806, name: 'Abyss Facemask', price: 1.49, chance: 1.2, color: '#22c55e', img: '/AbyssFacemask.png' };
const WOOD_ABYSS = { id: 807, name: 'Wood', price: 0.23, chance: 31, color: '#22c55e', img: '/wood.png' };
const SCRAP_ABYSS = { id: 808, name: 'Scrap', price: 0.01, chance: 66, color: '#94a3b8', img: '/scrap.webp' };

// Train Case Item Definitions
const TRAIN_DOOR = { id: 901, name: 'Incarceration Armored Door', price: 107.11, chance: 0.25, color: '#ef4444', img: '/10.png' };
const TRAIN_HAT = { id: 902, name: 'Train Conductor Hat', price: 24.09, chance: 2.75, color: '#a855f7', img: '/11.png' };
const TRAIN_SATCHEL = { id: 903, name: 'Military Satchel Charge', price: 8.11, chance: 5, color: '#3b82f6', img: '/12.png' };
const TRAIN_BARRICADE = { id: 904, name: 'Railway Barricade', price: 2.74, chance: 5, color: '#22c55e', img: '/13.png' };
const TRAIN_PANTS = { id: 905, name: 'Railway Engineer Pants', price: 1.50, chance: 25, color: '#22c55e', img: '/14.png' };
const TRAIN_JACKET = { id: 906, name: 'Railway Engineer Jacket', price: 1.17, chance: 62, color: '#22c55e', img: '/15.png' };

// Candy Case Item Definitions
const CANDY_DECOR = { id: 1001, name: 'Giant Candy Decor', price: 10.23, chance: 1, color: '#3b82f6', img: '/1.png' };
const CANDY_SAR = { id: 1002, name: 'Candyman SAR', price: 4.93, chance: 2, color: '#22c55e', img: '/2.png' };
const CANDY_AR = { id: 1003, name: 'Candy Hunter AR', price: 1.67, chance: 2, color: '#22c55e', img: '/3.png' };
const CANDY_EOKA = { id: 1004, name: 'Candy Cane Eoka', price: 1.18, chance: 3, color: '#22c55e', img: '/4.png' };
const CANDY_PICKAXE = { id: 1005, name: 'Christmas Candy Pickaxe', price: 1.11, chance: 4, color: '#22c55e', img: '/5.png' };
const CANDY_VENDING = { id: 1006, name: 'Gingerbread Candy Vending Machine', price: 1.04, chance: 4, color: '#22c55e', img: '/6.png' };
const CANDY_ARMOR = { id: 1007, name: 'Chocolate Easter Armor', price: 0.84, chance: 5, color: '#22c55e', img: '/7.png' };
const CANDY_COAT = { id: 1008, name: 'Santa\'s Coat', price: 0.75, chance: 26, color: '#22c55e', img: '/8.png' };
const CANDY_RUG = { id: 1009, name: 'Gingerbread Bear Rug', price: 0.03, chance: 53, color: '#22c55e', img: '/9.png' };

// Monsters Item Definitions
const ZIPPER_FACE = { id: 1301, name: 'Zipper Face', price: 454.89, chance: 0.05, color: '#ffb703', img: '/zippermask.png' };
const RAT_MASK = { id: 1302, name: 'Rat Mask', price: 19.48, chance: 0.5, color: '#ef4444', img: '/rat maskpng.png' };
const COMBAT_KNIFE = { id: 1303, name: 'Combat Knife', price: 18.84, chance: 0.75, color: '#ef4444', img: '/combat knife.png' };
const REPTILE_HUNTER = { id: 1304, name: 'Reptile Hunter', price: 11.06, chance: 1, color: '#a855f7', img: '/reptile hunter.png' };
const ZOMBIE_CAP = { id: 1305, name: 'Zombie Cap', price: 6.40, chance: 1, color: '#a855f7', img: '/zombie cap.png' };
const DRACULA_CAPE = { id: 1306, name: 'Dracula Cape', price: 5.81, chance: 1, color: '#a855f7', img: '/dracula cap.png' };
const ZOMBIE_HAMMER = { id: 1307, name: 'Zombie Hammer', price: 4.90, chance: 1, color: '#3b82f6', img: '/zombie hammer.png' };
const FRANKENSTEIN_HEAD = { id: 1308, name: 'Frankenstein Head', price: 4.66, chance: 3, color: '#3b82f6', img: '/frankenstein head.png' };
const DRACULA_MASK = { id: 1309, name: 'Dracula Mask', price: 4.00, chance: 3, color: '#3b82f6', img: '/dracula mask.png' };
const VAMPIRE_STONE = { id: 1310, name: 'Vampire Stone', price: 3.22, chance: 10, color: '#22c55e', img: '/vampire stone.png' };
const FRANKENSTEIN_FRIDGE = { id: 1311, name: 'Frankenstein Fridge', price: 2.94, chance: 10, color: '#22c55e', img: '/fridge1.png' };
const VAMPIRE_CROSSBOW = { id: 1312, name: 'Vampire Crossbow', price: 1.61, chance: 15, color: '#22c55e', img: '/vampirecrossbow.png' };
const FRANKENBAG = { id: 1313, name: 'Frankenbag', price: 1.21, chance: 15, color: '#94a3b8', img: '/frankenbag.png' };
const LITTLE_DRACULA_BOX = { id: 1314, name: 'Little Dracula Box', price: 0.89, chance: 10, color: '#94a3b8', img: '/lildraculabox.png' };
const DRACULA_FURNACE = { id: 1315, name: 'Dracula Furnace', price: 0.80, chance: 10, color: '#94a3b8', img: '/draculafurness.png' };
const ZOMBIE_FACEMASK = { id: 1316, name: 'Zombie Facemask', price: 0.55, chance: 18.7, color: '#94a3b8', img: '/zombie facemask.png' };

// Bunny Case Items
const BUNNY_GRAVE = { id: 1501, name: 'Freshly Dug Grave', price: 550.00, chance: 1.0, color: '#ffb703', img: '/freshlyduggrave.png' };
const BUNNY_EOKA = { id: 1502, name: 'Banana Eoka', price: 130.00, chance: 1.0, color: '#ef4444', img: '/bananeoka.png' };
const BUNNY_FARMER_HAT = { id: 1503, name: 'Farmer Hat', price: 17.94, chance: 18.0, color: '#a855f7', img: '/farmerhat.png' };
const BUNNY_KNIFE = { id: 1504, name: 'Carrot Knife', price: 3.03, chance: 5.0, color: '#22c55e', img: '/carrotknife.png' };
const BUNNY_BAG = { id: 1505, name: 'Cozy Bunny Bag', price: 2.52, chance: 4.3, color: '#22c55e', img: '/bunnybag.png' };
const BUNNY_DOOR = { id: 1506, name: 'Neon Carrot Door', price: 1.35, chance: 4.0, color: '#22c55e', img: '/neoncarrotdoor.png' };
const BUNNY_HAT = { id: 1507, name: 'Bunny Hat', price: 1.14, chance: 3.15, color: '#22c55e', img: '/bunnyhat.png' };
const BUNNY_FURNACE = { id: 1508, name: 'Bunny Furnace', price: 0.98, chance: 3.68, color: '#22c55e', img: '/bunnyfurness.png' };
const BUNNY_BALACLAVA = { id: 1509, name: 'Carrot Balaclava', price: 0.86, chance: 3.15, color: '#22c55e', img: '/carrit.png' };
const BUNNY_BLAST = { id: 1510, name: 'Bunny Blast AR', price: 0.80, chance: 3.37, color: '#22c55e', img: '/bunnyblast.png' };
const BUNNY_WOOD_DOOR = { id: 1511, name: 'Funny Bunny Wood Door', price: 0.60, chance: 53.35, color: '#22c55e', img: '/funnybunny.png' };

// Hot Case Items
const HOT_SWORD = { id: 1601, name: 'Hot-tempered Longsword', price: 245.39, chance: 0.965, color: '#ffb703', img: '/hottemperedsword.png' };
const HOT_SHOTGUN = { id: 1602, name: 'Heat Double Shotgun', price: 16.09, chance: 3.806, color: '#ef4444', img: '/heatdoubleshotgun.png' };
const HOT_REVOLVER = { id: 1603, name: 'Hot-tempered Revolver', price: 11.61, chance: 4.269, color: '#ef4444', img: '/hotrevolver.png' };
const HOT_GRENADE = { id: 1604, name: 'Fire and Brimstone Grenade', price: 10.86, chance: 5.974, color: '#a855f7', img: '/firegrenade.png' };
const HOT_POTATO = { id: 1605, name: 'Hot Potato', price: 5.95, chance: 8.023, color: '#a855f7', img: '/hotpotato.png' };
const HOT_GLOVES = { id: 1606, name: 'OVERHEAT Roadsign Gloves', price: 1.32, chance: 5.459, color: '#3b82f6', img: '/gloves.png' };
const HOT_FURNACE = { id: 1607, name: 'Hellfire Furnace', price: 1.22, chance: 7.378, color: '#3b82f6', img: '/hellfirefurness.png' };
const HOT_HOODIE = { id: 1608, name: 'OVERHEAT Hoodie', price: 0.96, chance: 6.589, color: '#22c55e', img: '/firehoodie.png' };
const HOT_PANTS = { id: 1609, name: 'OVERHEAT Pants', price: 0.80, chance: 6.731, color: '#22c55e', img: '/firepants.png' };
const HOT_BOMB = { id: 1610, name: 'Brothers of Fire', price: 0.66, chance: 9.555, color: '#22c55e', img: '/bomboffire.png' };
const HOT_SCRAP = { id: 1611, name: 'Scrap', price: 0.01, chance: 41.251, color: '#94a3b8', img: '/scrap.webp' };

// Frostbite Case Items
const FROST_MASK = { id: 1701, name: 'Frostbite', price: 128.79, chance: 1, color: '#ef4444', img: '/icemask.png' };
const FROST_REVOLVER = { id: 1702, name: 'Frostbitten Revolver', price: 19.90, chance: 23, color: '#a855f7', img: '/frostbittenrevolver.png' };
const FROST_AK = { id: 1703, name: 'Cold Hunter AK', price: 16.18, chance: 23, color: '#a855f7', img: '/coldhunterak.png' };
const FROST_PYTHON = { id: 1704, name: 'Cold Hunter Python', price: 9.64, chance: 25, color: '#3b82f6', img: '/goldpython.png' };
const FROST_HOODIE = { id: 1705, name: 'Winter Hoodie', price: 2.88, chance: 15, color: '#22c55e', img: '/hoodiecold.png' };
const FROST_JACKET = { id: 1706, name: 'Snowcamo Jacket', price: 1.53, chance: 8, color: '#22c55e', img: '/snowc2amo.png' };
const FROST_PANTS = { id: 1707, name: 'Snow Camo Pants', price: 0.66, chance: 5, color: '#22c55e', img: '/snowpants.png' };

// Lost Souls Case Items
const UNDEAD_DOOR = { id: 1801, name: 'Undead Door', price: 2500.00, chance: 0.05, color: '#eab308', img: '/undeaddoor.png' };
const LOST_PUNISHMENT = { id: 1802, name: 'Punishment Mask', price: 2390.00, chance: 0.05, color: '#eab308', img: '/punishmentmask.png' };
const LOST_CLOWN = { id: 1803, name: 'Creepy Clown', price: 500.00, chance: 0.5, color: '#a855f7', img: '/creepyclown.png' };
const LOST_ZIPPER = { id: 1804, name: 'Zipper Face', price: 454.89, chance: 0.5, color: '#a855f7', img: '/zippermask.png' };
const LOST_SACRIFICIAL = { id: 1805, name: 'Sacrificial Mask', price: 19.51, chance: 20, color: '#3b82f6', img: '/SacrificialMask.png' };
const LOST_ZOMBIE = { id: 1806, name: 'Zombie Facemask', price: 0.55, chance: 30, color: '#22c55e', img: '/zombie facemask.png' };
const LOST_SCRAP = { id: 1807, name: 'Scrap', price: 0.01, chance: 48.9, color: '#94a3b8', img: '/scrap.webp' };

const MOCK_CASES = [
  { id: 1, name: '1% BIG GRIN', price: 15.00, img: '/1_grin.png', items: [{ ...BIG_GRIN, chance: 1, img: '/biggrin.png' }, { ...SCRAP, chance: 99 }] },
  { id: 2, name: '10% BIG GRIN', price: 125.00, img: '/10_grin.png', items: [{ ...BIG_GRIN, chance: 10, img: '/biggrin.png' }, { ...SCRAP, chance: 90 }] },
  { id: 3, name: 'WILD WEST', price: 0.50, img: '/Wildwest.png', items: [
    JAWBOY, PHANTOM_REVOLVER, MOROCCO_PYTHON, SLAUGHTER, SHERIFF_HAT, SHERIFF_SUIT, HUNTED_HAT, COWBOY_HAT, BATTLE_STEEL, COWBOY_SHIRT, COWBOY_PANTS, BURN_PANTS, GOLDEN_FANG, SNOW_CAMO, FOREST_CAMO, CLOTH, { ...SCRAP, chance: 29 }
  ] },
  { id: 4, name: 'FACEMASK FLIP', price: 60.00, img: '/facemaskflip.png', manualRisk: 20, items: [
    { ...BIG_GRIN, id: 301, chance: 0.5 },
    { ...FROSTBITE_FACEMASK, id: 302, chance: 4.5 },
    { ...STAINLESS_FACEMASK, id: 303, chance: 45 },
    { ...BLUE_SHIRT, id: 304, chance: 20 },
    { ...CLOTH, id: 305, chance: 20 },
    { ...SCRAP, id: 306, chance: 10 }
  ] },
  { id: 5, name: 'FESTIVE EGG', price: 6.00, img: '/festivecase.png', manualRisk: 50, items: [
    EGG_BLUE, EGG_RED, GOLDEN_DRAGON, CELESTIAL_EGG, THUNDERGOLD_CHEST, { ...SCRAP, chance: 33.5 }
  ] },
  { id: 7, name: 'FORTUNE', price: 500.00, img: '/FORTUNE.png', items: [
    { ...PUNISHMENT_MASK, chance: 2 },
    { ...ALIEN_RELIC, chance: 2 },
    { ...BIG_GRIN, chance: 5 },
    { ...TEMPERED_MP5, chance: 10 },
    { ...CREEPY_CLOWN, chance: 10 },
    { ...LEGENDARY_GOLD, chance: 71 }
  ] },
  { id: 8, name: 'Abyss', price: 0.25, img: '/abyss.png', items: [
    RAVEN, DARK_ARTS_ICEPICK, SACRIFICIAL_MASK_ABYSS, ABYSS_SLEEPING_BAG, ABYSS_THOMPSON, ABYSS_FACEMASK, WOOD_ABYSS, SCRAP_ABYSS
  ] },
  { id: 9, name: 'Train', price: 3.00, img: '/train case pic.webp', items: [
    TRAIN_DOOR, TRAIN_HAT, TRAIN_SATCHEL, TRAIN_BARRICADE, TRAIN_PANTS, TRAIN_JACKET
  ] },
  { id: 10, name: 'Candy', price: 0.70, img: '/candy case pic.png', items: [
    CANDY_DECOR, CANDY_SAR, CANDY_AR, CANDY_EOKA, CANDY_PICKAXE, CANDY_VENDING, CANDY_ARMOR, CANDY_COAT, CANDY_RUG
  ] },
  { id: 11, name: 'Oil Rig', price: 45.00, img: '/oilrigcase.png', items: [
    { ...TEMPERED_MP5, chance: 3, color: '#ffb703' },
    { ...TEMPERED_MASK, chance: 6, color: '#ffb703' },
    { id: 1101, name: 'Tempered AK47', price: 15.00, chance: 20, color: '#a855f7', img: '/tempered ak.png' },
    { id: 1102, name: 'Blackout L96', price: 3.00, chance: 40, color: '#3b82f6', img: '/blackout l96.png' },
    { id: 1103, name: 'Scientist Suit', price: 1.50, chance: 20, color: '#3b82f6', img: '/scientist suit.webp' },
    { id: 1104, name: 'Green Keycard', price: 0.20, chance: 11, color: '#22c55e', img: '/greenkeycard.png' }
  ] },
  { id: 12, name: 'Keycards', price: 3.00, img: '/keycard_red.png', items: [
    { id: 1201, name: 'Purple Keycard', price: 50.00, chance: 1, color: '#ffb703', img: '/5e39b2b5edf4b.png' },
    { id: 1202, name: 'Red Keycard', price: 15.00, chance: 5, color: '#ffb703', img: '/keycard_red.png' },
    { id: 1203, name: 'Blue Keycard', price: 5.00, chance: 15, color: '#a855f7', img: '/keycard_blue.png' },
    { id: 1103, name: 'Scientist Suit', price: 1.50, chance: 25, color: '#3b82f6', img: '/scientist suit.webp' },
    { id: 1104, name: 'Green Keycard', price: 0.20, chance: 54, color: '#22c55e', img: '/greenkeycard.png' }
  ] },
  { id: 13, name: 'Monsters', price: 2.65, img: '/monsters.png', items: [
    ZIPPER_FACE, RAT_MASK, COMBAT_KNIFE, REPTILE_HUNTER, ZOMBIE_CAP, DRACULA_CAPE, ZOMBIE_HAMMER, FRANKENSTEIN_HEAD, DRACULA_MASK, VAMPIRE_STONE, FRANKENSTEIN_FRIDGE, VAMPIRE_CROSSBOW, FRANKENBAG, LITTLE_DRACULA_BOX, DRACULA_FURNACE, ZOMBIE_FACEMASK
  ] },
  { id: 14, name: '67Jimmy', price: 2.50, img: '/67jimmy.png', manualRisk: 10, items: [
    { id: 1401, name: 'Hobo Barrel', price: 20.00, chance: 2, color: '#ffb703', img: '/hobobarrel.png' },
    { id: 1402, name: 'Double Barrel Shotgun', price: 4.50, chance: 13, color: '#a855f7', img: '/shotgun.double.png' },
    { id: 1403, name: 'Eoka Pistol', price: 3.80, chance: 25, color: '#3b82f6', img: '/pistol.eoka.png' },
    { id: 1404, name: 'Rock', price: 0.03, chance: 60, color: '#94a3b8', img: '/Rock_icon.webp' }
  ] },
  { id: 15, name: 'Bunny', price: 12.00, img: '/bunny.png', items: [
    BUNNY_GRAVE, BUNNY_EOKA, BUNNY_FARMER_HAT, BUNNY_KNIFE, BUNNY_BAG, BUNNY_DOOR, BUNNY_HAT, BUNNY_FURNACE, BUNNY_BALACLAVA, BUNNY_BLAST, BUNNY_WOOD_DOOR
  ] },
  { id: 16, name: 'Hot', price: 5.50, img: '/Hot.png', manualRisk: 50, items: [
    HOT_SWORD, HOT_SHOTGUN, HOT_REVOLVER, HOT_GRENADE, HOT_POTATO, HOT_GLOVES, HOT_FURNACE, HOT_HOODIE, HOT_PANTS, HOT_BOMB, HOT_SCRAP
  ] },
  { id: 17, name: 'Frostbite', price: 14.00, img: '/icemask.png', manualRisk: 30, items: [
    FROST_MASK, FROST_REVOLVER, FROST_AK, FROST_PYTHON, FROST_HOODIE, FROST_JACKET, FROST_PANTS
  ] },
    { id: 18, name: 'Lost Souls', price: 15.00, img: '/Lost Souls.png', manualRisk: 95, themeColor: '#00f2ff', items: [
    UNDEAD_DOOR, LOST_PUNISHMENT, LOST_CLOWN, LOST_ZIPPER, LOST_SACRIFICIAL, LOST_ZOMBIE, LOST_SCRAP
  ] }
];

const CasePreviewModal = ({ caseData, onClose, onGoToCase }) => {
    if (!caseData) return null;

    const totalChance = caseData.items.reduce((acc, item) => acc + item.chance, 0);
    let accumulated = 0;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }} onClick={onClose}>
            <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                style={{ background: '#121217', width: '100%', maxWidth: '1000px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.05)' }} 
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ padding: '24px 32px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <img src={caseData.img} alt="" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800' }}>
                                <span style={{ color: 'white', fontWeight: '900' }}>{caseData.name}</span> contains
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                <ScrapIcon size={16} color="#ffb703" />
                                <span style={{ fontSize: '18px', fontWeight: '900', color: 'white' }}>{caseData.price.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <button 
                            onClick={onGoToCase}
                            style={{ background: '#ffb703', border: 'none', color: '#000', padding: '12px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '900', cursor: 'pointer', transition: '0.2s' }}
                            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 183, 3, 0.4)'; }}
                            onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                            Go to case
                        </button>
                        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', padding: '8px' }} onMouseEnter={e => e.currentTarget.style.color = 'white'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}>
                            <FaTimes size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '32px', maxHeight: '70vh', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                    {caseData.items.map((item, idx) => {
                        const startRoll = Math.floor((accumulated / totalChance) * 100000) + 1;
                        const endRoll = Math.floor(((accumulated + item.chance) / totalChance) * 100000);
                        accumulated += item.chance;
                        
                        return (
                            <div key={idx} style={{ 
                                background: 'rgba(255,255,255,0.02)', 
                                borderRadius: '12px', 
                                border: '1px solid rgba(255,255,255,0.03)', 
                                borderBottom: `2px solid ${item.color}`,
                                padding: '16px 12px', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: `inset 0 0 20px ${item.color}05`
                            }}>
                                {/* Top Badges */}
                                <div style={{ position: 'absolute', top: '12px', left: '12px', fontSize: '10px', fontWeight: '900', color: item.color, textShadow: `0 0 5px ${item.color}44` }}>{item.chance.toFixed(2)}%</div>
                                <div style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '10px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.05)', padding: '2px 4px', borderRadius: '4px' }}>FN</div>

                                {/* Image & Shield */}
                                <div style={{ position: 'relative', width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '15px 0' }}>
                                    <div style={{ position: 'absolute', width: '70%', height: '70%', background: item.color, filter: 'blur(25px)', opacity: 0.1, borderRadius: '50%' }} />
                                    <svg viewBox="0 0 100 100" style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0.3 }}>
                                        <path d="M50 5 L90 25 L90 75 L50 95 L10 75 L10 25 Z" fill="none" stroke={item.color} strokeWidth="3" />
                                    </svg>
                                    <img src={item.img} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain', position: 'relative', zIndex: 1, filter: `drop-shadow(0 0 15px ${item.color}66)` }} />
                                </div>

                                {/* Info */}
                                <div style={{ fontSize: '10px', fontWeight: '900', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '4px' }}>Skin</div>
                                <div style={{ fontSize: '13px', fontWeight: '900', color: item.color, textAlign: 'center', marginBottom: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{item.name}</div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                                    <ScrapIcon size={12} color="#ffb703" />
                                    <span style={{ fontSize: '14px', fontWeight: '900', color: 'white' }}>{item.price.toFixed(2)}</span>
                                </div>

                                <div style={{ fontSize: '9px', fontWeight: '800', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase' }}>
                                    {startRoll.toLocaleString()} - {endRoll.toLocaleString()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </motion.div>
        </div>
    );
};

const ScrapIcon = ({ size = 14 }) => (
    <img 
        src="/scrap.webp" 
        alt="scrap" 
        style={{ width: size, height: size, objectFit: 'contain' }} 
    />
);

const CaseDetail = ({ caseData, onBack, favorites, onToggleFavorite }) => {
    const navigate = useNavigate();
    const isFavorited = favorites.includes(caseData.id);
    const [selectedCount, setSelectedCount] = useState(1);
    const [isUnboxing, setIsUnboxing] = useState(false);
    const [reels, setReels] = useState([]); // Array of item arrays
    const [winnerIndex, setWinnerIndex] = useState(0);
    const [roundKey, setRoundKey] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [isFastMode, setIsFastMode] = useState(false);
    const [reelDurations, setReelDurations] = useState([]); // Array of randomized durations
    const [reelOffsets, setReelOffsets] = useState([]); // Randomized x/y offsets for 'loose' look
    const { user, setIsAuthModalOpen, activeBalance } = useContext(UserContext);
    const timeoutsRef = useRef([]);

    const clearAllTimeouts = () => {
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];
    };

    const addTimeout = (fn, delay) => {
        const id = setTimeout(fn, delay);
        timeoutsRef.current.push(id);
        return id;
    };

    useEffect(() => {
        return () => clearAllTimeouts();
    }, []);

    useEffect(() => {
        clearAllTimeouts();
        const initialReels = Array.from({ length: selectedCount }, () => generateInitialReel(120));
        setReels(initialReels);
        setWinnerIndex(0);
        setShowResult(false);
        setIsUnboxing(false);
    }, [caseData, selectedCount]);

    const handleCountChange = (num) => {
        if (isUnboxing) return;
        clearAllTimeouts();
        setSelectedCount(num);
        setWinnerIndex(0);
        setShowResult(false);
        const initialReels = Array.from({ length: num }, () => generateInitialReel(120));
        setReels(initialReels);
    };

    // Buffer to handle balance updates - prevents premature socket updates
    useEffect(() => {
        if (isUnboxing) {
            window._isCaseOpening = true;
        } else {
            // Wait a tiny bit more to ensure all animations are fully settled
            const t = setTimeout(() => {
                window._isCaseOpening = false;
            }, 500);
            return () => clearTimeout(t);
        }
    }, [isUnboxing]);

    const generateInitialReel = (count) => {
        const pool = caseData.items;
        return Array.from({ length: count }, () => pool[Math.floor(Math.random() * pool.length)]);
    };

    const playTick = () => {
        const audio = new Audio('/tick.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {}); 
    };

    const playBeep = () => {
        const audio = new Audio('/beep.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {}); 
    };

    const handleActualSpin = async () => {
        if (!user) {
            setIsAuthModalOpen(true);
            return;
        }
        if (isUnboxing) return;
        
        const totalCost = caseData.price * selectedCount;
        const currentBalance = activeBalance === 'promo' ? (user.promoBalance || 0) : user.balance;

        if (currentBalance < totalCost) {
            if (window._addToast) {
                window._addToast({ type: 'error', title: 'BALANCE ERROR', message: `Insufficient ${activeBalance === 'promo' ? 'promo ' : ''}scrap balance` });
            } else {
                alert(`Insufficient ${activeBalance === 'promo' ? 'promo ' : ''}balance`);
            }
            return;
        }

        // Capture fast mode at spin-start so closures below always see the same value
        const fastMode = isFastMode;

        try {
            // 1. PREP FAST UI RESPONSE (Immediate feedback)
            setShowResult(false);
            setRoundKey(prev => prev + 1);
            setIsUnboxing(true);

            // Deduct immediately in UI for responsive feel
            const balanceKey = activeBalance === 'promo' ? 'promoBalance' : 'balance';
            window.dispatchEvent(new CustomEvent('balanceUpdate', { 
                detail: { [balanceKey]: currentBalance - totalCost } 
            }));


            // Use pool for initial "tension" reel before results arrive
            const pool = caseData.items;
            const tempReels = Array.from({ length: selectedCount }, () => {
                return Array.from({ length: 200 }, () => ({
                    ...pool[Math.floor(Math.random() * pool.length)],
                    dropKey: Math.random().toString()
                }));
            });
            setReels(tempReels);

            // Set initial durations and offsets to start the 'loose' mechanical spin immediately
            const baseDur = fastMode ? 1200 : 7000;
            const initialDurations = Array(selectedCount).fill(0).map(() => baseDur + (Math.random() * (fastMode ? 300 : 2500)));
            const initialOffsets = Array(selectedCount).fill(0).map(() => (Math.random() - 0.5) * 60); 
            
            setReelDurations(initialDurations);
            setReelOffsets(initialOffsets);
            
            // Kick the reel to position 2 immediately so the spin has a clear start
            setWinnerIndex(2);

            // 2. CALL BACKEND (During the 'tension' phase)
            const res = await gameAPI.cases.open({
                caseId: caseData.id.toString(),
                count: selectedCount,
                usePromoBalance: activeBalance === 'promo'
            }).catch(err => {
                // If backend fails, revert UI state
                setIsUnboxing(false);
                window.dispatchEvent(new CustomEvent('balanceUpdate', { 
                    detail: { [balanceKey]: currentBalance } 
                }));
                throw err;
            });

            if (res.data.success) {
                const { results } = res.data;
                const winIdx = 100;
                
                // 3. INJECT THE REAL RESULTS into the reel
                const updatedReels = results.map((winner, rIdx) => {
                    return Array.from({ length: 200 }, (_, i) => {
                        const baseItem = i === winIdx ? winner : pool[Math.floor(Math.random() * pool.length)];
                        return { 
                            ...baseItem, 
                            isWinner: i === winIdx,
                            dropKey: `${roundKey}-${rIdx}-${i}`
                        };
                    });
                });

                setReels(updatedReels);

                // 4. LAUNCH TO FINAL DESTINATION
                addTimeout(() => {
                    setWinnerIndex(winIdx);
                    
                    const maxDuration = Math.max(...initialDurations);
                    let elapsed = 0;
                    const playTickLoop = () => {
                        if (elapsed >= maxDuration) return;
                        playTick();
                        const progress = elapsed / maxDuration;
                        const nextTick = 40 + (progress * 500);
                        elapsed += nextTick;
                        addTimeout(playTickLoop, nextTick);
                    };
                    playTickLoop();

                    addTimeout(() => {
                        setIsUnboxing(false);
                        setShowResult(true);
                        setReelOffsets(Array(selectedCount).fill(0)); // SNAP BACK TO CENTER
                        playBeep();
                        
                        // Sync final balance from backend
                        const { newBalance, newPromoBalance, isPromo: resIsPromo } = res.data;
                        if (resIsPromo) {
                            window.dispatchEvent(new CustomEvent('balanceUpdate', { 
                                detail: { promoBalance: newPromoBalance, isFinal: true } 
                            }));
                        } else {
                            window.dispatchEvent(new CustomEvent('balanceUpdate', { 
                                detail: { balance: newBalance, isFinal: true } 
                            }));
                        }
                    }, maxDuration);
                }, 100); // Small buffer to ensure reel DOM update happens before animation
            }
        } catch (error) {
            setIsUnboxing(false);
            console.error('Actual spin error:', error);
            alert(apiHelpers.handleError(error));
        }
    };

    const handleDemoSpin = () => {
        if (isUnboxing) return;

        // Capture fast mode at spin-start so closures always see the same value
        const fastMode = isFastMode;

        const winIdx = 100;
        const pool = caseData.items;
        
        const newReels = Array.from({ length: selectedCount }, () => {
            let winner;
            const randomRoll = Math.random() * 100;
            let cumulative = 0;
            for (const item of pool) {
                cumulative += item.chance;
                if (randomRoll <= cumulative) {
                    winner = item;
                    break;
                }
            }
            if (!winner) winner = pool[pool.length - 1];

            return Array.from({ length: 200 }, (_, i) => {
                const baseItem = i === winIdx ? winner : pool[Math.floor(Math.random() * pool.length)];
                return { 
                    ...baseItem, 
                    isWinner: i === winIdx,
                    dropKey: `${roundKey}-${i}`
                };
            });
        });

        // 1. PREP — identical to handleActualSpin for visual consistency
        setShowResult(false);
        setRoundKey(prev => prev + 1);
        setIsUnboxing(true);

        // Build durations + offsets exactly like the real spin so animations look the same
        const baseDur = fastMode ? 1200 : 7000;
        const demoDurations = Array(selectedCount).fill(0).map(() => baseDur + (Math.random() * (fastMode ? 300 : 2500)));
        const demoOffsets = Array(selectedCount).fill(0).map(() => (Math.random() - 0.5) * 60);

        setReelDurations(demoDurations);
        setReelOffsets(demoOffsets);
        setReels(newReels);

        // Kick to position 2 first (same as real spin) so the jump to 100 feels identical
        setWinnerIndex(2);

        // 2. SPIN — small buffer to let DOM apply reel + winnerIndex=2 before launching to 100
        addTimeout(() => {
            setWinnerIndex(winIdx);
            
            const maxDuration = Math.max(...demoDurations);
            let elapsed = 0;
            const playTickLoop = () => {
                if (elapsed >= maxDuration) return;
                playTick();
                const progress = elapsed / maxDuration;
                const nextTick = 40 + (progress * 500);
                elapsed += nextTick;
                addTimeout(playTickLoop, nextTick);
            };
            playTickLoop();

            addTimeout(() => {
                setShowResult(true);
                setIsUnboxing(false);
                setReelOffsets(Array(selectedCount).fill(0)); // SNAP BACK TO PERFECT CENTER
                playBeep();
            }, maxDuration);
        }, 100); // Same buffer as real spin
    };

    const getCaseRisk = (caseObj) => {
        if (!caseObj) return 0;
        if (caseObj.manualRisk !== undefined) return caseObj.manualRisk;
        const totalChance = caseObj.items.reduce((acc, item) => acc + item.chance, 0);
        const winChance = caseObj.items
            .filter(i => i.price >= caseObj.price)
            .reduce((acc, item) => acc + (item.chance / totalChance), 0);
        return Math.max(5, (1 - winChance) * 100);
    };

    const formatPrice = (p) => p ? p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';

    return (
        <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
            {/* 1. TOP HEADER */}
            <div style={{ position: 'relative', marginBottom: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button 
                    onClick={onBack} 
                    style={{ position: 'absolute', left: 0, top: 0, background: 'transparent', border: 'none', color: '#64748b', fontSize: '13px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', textTransform: 'uppercase' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ffb703'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                >
                    <FaChevronLeft size={10} /> Back to cases
                </button>

                <div style={{ textAlign: 'center' }}>
                    <img src={caseData.img} alt="" style={{ width: '80px', height: '80px', objectFit: 'contain', filter: `drop-shadow(0 0 20px ${BRAND_PURPLE}44)` }} />
                    <h3 style={{ fontSize: '24px', fontWeight: '900', color: 'white', margin: '15px 0 0', textTransform: 'uppercase', letterSpacing: '1px' }}>{caseData.name}</h3>
                </div>

                <div style={{ position: 'absolute', right: 0, top: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div 
                        onClick={() => onToggleFavorite(caseData.id)}
                        style={{ color: isFavorited ? '#ff3e3e' : 'rgba(255,255,255,0.4)', fontSize: '13px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.03)', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', transition: '0.2s', border: isFavorited ? '1px solid rgba(255, 62, 62, 0.2)' : '1px solid transparent' }}
                    >
                        <FaHeart size={14} /> {isFavorited ? 'Favorited' : 'Favorite'}
                    </div>
                    <button 
                        onClick={() => navigate('/fairness')}
                        style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'rgba(255,255,255,0.6)', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                    >
                        <FaShieldAlt size={16} /> Fairness
                    </button>
                </div>
            </div>

            {/* 2. SOPHISTICATED REEL CONTAINER */}
            <div style={{ 
                background: '#0d0d12', 
                borderRadius: '20px', 
                border: '1px solid rgba(255,255,255,0.03)', 
                position: 'relative', 
                marginBottom: '30px',
                minHeight: selectedCount === 1 ? '450px' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                overflow: 'hidden',
                boxShadow: 'inset 0 0 100px rgba(0,0,0,0.8)',
                padding: selectedCount === 1 ? '0' : '20px'
            }}>
                {/* Single Case Central Marker */}
                {selectedCount === 1 && (
                    <>
                        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 100, color: '#f59e0b', filter: 'drop-shadow(0 0 10px #f59e0b44)', pointerEvents: 'none' }}>
                            <FaCaretDown size={32} />
                        </div>
                        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 100, color: '#f59e0b', filter: 'drop-shadow(0 0 10px #f59e0b44)', pointerEvents: 'none' }}>
                            <FaCaretUp size={32} />
                        </div>
                    </>
                )}

                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: selectedCount === 1 ? '1fr' : (selectedCount <= 3 ? '1fr' : '1fr 1fr'),
                    gap: '10px',
                    width: '100%'
                }}>
                    {reels.map((reelItems, reelIdx) => {
                        const itemW = selectedCount === 1 ? 260 : 180;
                        const itemH = selectedCount === 1 ? 400 : 140;
                        
                        return (
                            <div key={`${roundKey}-${reelIdx}`} style={{ 
                                height: `${itemH + 20}px`,
                                background: 'rgba(255,255,255,0.01)', 
                                border: '1px solid rgba(255,255,255,0.02)',
                                borderRadius: '12px',
                                position: 'relative', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                overflow: 'hidden'
                            }}>
                                {/* Multi-case individual marker line */}
                                {selectedCount > 1 && (
                                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '2px', background: 'linear-gradient(to bottom, transparent, #f59e0b, transparent)', zIndex: 100, opacity: 0.5, pointerEvents: 'none' }} />
                                )}

                                {/* DriftContainer: Handles the 'loose' mechanical misalignment */}
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transform: `translateX(${reelOffsets[reelIdx] || 0}px)`,
                                    transition: showResult ? 'transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.25)' : 'none',
                                }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        transform: `translateX(calc(50% - ${itemW/2}px - (${winnerIndex} * ${itemW}px)))`,
                                        transition: isUnboxing 
                                            ? `transform ${reelDurations[reelIdx] || (isFastMode ? 1200 : 7000)}ms cubic-bezier(0.1, 0.45, 0, 1.0)` 
                                            : 'transform 0.5s ease-out',
                                    }}>
                                        {reelItems.map((item, i) => (
                                            <motion.div 
                                                key={i}
                                                animate={{ 
                                                    scale: (showResult && item.isWinner) ? 1.1 : 1,
                                                    opacity: showResult ? (item.isWinner ? 1 : 0.4) : 1,
                                                }}
                                                style={{ 
                                                    height: `${itemH}px`, 
                                                    minWidth: `${itemW}px`,
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    position: 'relative'
                                                }}
                                            >
                                                <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {/* Win Aura */}
                                                    {(showResult && item.isWinner) && (
                                                        <div style={{ position: 'absolute', width: '150%', height: '150%', background: `radial-gradient(circle, ${item.color}33 0%, transparent 70%)`, filter: 'blur(20px)', zIndex: -1 }} />
                                                    )}
                                                    
                                                    {/* SVG Shield Background */}
                                                    <svg width="80%" height="80%" viewBox="0 0 100 100" style={{ position: 'absolute', zIndex: 0, opacity: 0.1 }}>
                                                        <path d="M50 5 L90 25 L90 75 L50 95 L10 75 L10 25 Z" fill="none" stroke={item.color} strokeWidth="2" />
                                                    </svg>

                                                    <img src={item.img} alt="" style={{ width: selectedCount === 1 ? '100px' : '70px', height: selectedCount === 1 ? '100px' : '70px', objectFit: 'contain', filter: (showResult && item.isWinner) ? `drop-shadow(0 0 20px ${item.color}88) brightness(1.2)` : 'none' }} />
                                                </div>

                                                {/* Winner Label Overlay */}
                                                {(showResult && item.isWinner) && (
                                                    <motion.div 
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        style={{ position: 'absolute', bottom: selectedCount === 1 ? '40px' : '10px', textAlign: 'center', width: '100%', zIndex: 1000 }}
                                                    >
                                                        <div style={{ fontSize: selectedCount === 1 ? '16px' : '11px', fontWeight: '900', color: item.color, textTransform: 'uppercase', textShadow: '0 2px 10px rgba(0,0,0,0.8)', marginBottom: '2px' }}>{item.name}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                            <ScrapIcon size={selectedCount === 1 ? 14 : 11} />
                                                            <span style={{ fontSize: selectedCount === 1 ? '18px' : '13px', fontWeight: '900', color: 'white' }}>{item.price.toFixed(2)}</span>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 3. CONTROLS BAR */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '25px', marginBottom: '50px', background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.02)' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {[1, 2, 3, 4].map(num => (
                        <button key={num} onClick={() => handleCountChange(num)} disabled={isUnboxing} style={{ width: '42px', height: '42px', background: selectedCount === num ? '#ffb703' : 'rgba(255,255,255,0.04)', border: 'none', borderRadius: '8px', color: selectedCount === num ? '#000' : 'rgba(255,255,255,0.4)', fontWeight: '900', cursor: 'pointer', transition: '0.2s' }}>
                            {num}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <button onClick={handleActualSpin} disabled={isUnboxing} style={{ background: '#ffb703', border: 'none', color: '#000', padding: '0 40px', height: '50px', borderRadius: '10px', fontSize: '16px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', transition: '0.2s', boxShadow: '0 4px 15px rgba(255, 183, 3, 0.2)' }}>
                        {isUnboxing ? <FaSync className="fa-spin" /> : 'Unbox'}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(0,0,0,0.1)', padding: '4px 12px', borderRadius: '6px' }}>
                            <ScrapIcon size={14} color="#000" />
                            {formatPrice(caseData.price * selectedCount)}
                        </div>
                    </button>
                    <button onClick={handleDemoSpin} disabled={isUnboxing} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'rgba(255,255,255,0.5)', padding: '0 25px', height: '50px', borderRadius: '10px', fontSize: '14px', fontWeight: '900', cursor: 'pointer' }}>Demo spin</button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', paddingLeft: '15px', borderLeft: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Quick</span>
                        <div onClick={() => !isUnboxing && setIsFastMode(!isFastMode)} style={{ width: '44px', height: '22px', background: isFastMode ? '#ffb703' : 'rgba(255,255,255,0.08)', borderRadius: '11px', position: 'relative', cursor: 'pointer', transition: '0.2s' }}>
                            <div style={{ position: 'absolute', top: '3px', left: isFastMode ? '25px' : '3px', width: '16px', height: '16px', background: 'white', borderRadius: '50%', transition: '0.2s' }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. ITEM GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
                {caseData.items.map((item, idx) => (
                    <div key={idx} style={{ 
                        background: 'rgba(255,255,255,0.02)', 
                        borderRadius: '12px', 
                        border: '1px solid rgba(255,255,255,0.03)', 
                        borderBottom: `3px solid ${item.color}`,
                        padding: '20px 12px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: `inset 0 0 25px ${item.color}05`
                    }}>
                        <div style={{ position: 'absolute', top: '12px', left: '12px', fontSize: '11px', fontWeight: '900', color: item.color }}>{item.chance.toFixed(2)}%</div>
                        <div style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '10px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.06)', padding: '3px 6px', borderRadius: '4px' }}>FN</div>

                        <div style={{ position: 'relative', width: '100px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '15px 0' }}>
                            <div style={{ position: 'absolute', width: '70%', height: '70%', background: item.color, filter: 'blur(25px)', opacity: 0.1, borderRadius: '50%' }} />
                            <svg viewBox="0 0 100 100" style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0.3 }}>
                                <path d="M50 5 L90 25 L90 75 L50 95 L10 75 L10 25 Z" fill="none" stroke={item.color} strokeWidth="3" />
                            </svg>
                            <img src={item.img} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain', position: 'relative', zIndex: 1, filter: `drop-shadow(0 0 15px ${item.color}44)` }} />
                        </div>

                        <div style={{ fontSize: '11px', fontWeight: '900', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '6px' }}>Skin</div>
                        <div style={{ fontSize: '14px', fontWeight: '900', color: item.color, textAlign: 'center', marginBottom: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{item.name}</div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(0,0,0,0.15)', padding: '6px 16px', borderRadius: '8px' }}>
                            <ScrapIcon size={12} color="#ffb703" />
                            <span style={{ fontSize: '14px', fontWeight: '900', color: 'white' }}>{item.price.toFixed(2)}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const getCaseRisk = (caseObj) => {
  if (!caseObj || !caseObj.items) return 50;
  if (caseObj.manualRisk !== undefined) return caseObj.manualRisk;
  const totalChance = caseObj.items.reduce((acc, item) => acc + item.chance, 0);
  const winChance = caseObj.items
    .filter(i => i.price >= caseObj.price)
    .reduce((acc, item) => acc + (item.chance / totalChance), 0);
  return Math.max(5, Math.min(95, (1 - winChance) * 100));
};

const ComingSoonToast = ({ message, onClose }) => (
    <motion.div 
        initial={{ opacity: 0, y: 50, x: '-50%' }}
        animate={{ opacity: 1, y: 0, x: '-50%' }}
        exit={{ opacity: 0, y: 50, x: '-50%' }}
        style={{ position: 'fixed', bottom: '100px', left: '50%', background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '15px 30px', borderRadius: '14px', zIndex: 10000, fontWeight: '900', fontSize: '14px', boxShadow: '0 10px 40px rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', gap: '15px' }}
    >
        <FaBolt color={THEME_COLOR} /> {message}
    </motion.div>
);

const Cases = () => {
    const [search, setSearch] = useState('');
    const [previewCase, setPreviewCase] = useState(null);
    const [selectedCase, setSelectedCase] = useState(null);
    const [sortOrder, setSortOrder] = useState('high');
    const [activeTab, setActiveTab] = useState('original');
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('case_favorites');
        return saved ? JSON.parse(saved) : [];
    });
    const [showSort, setShowSort] = useState(false);
    const [riskRange, setRiskRange] = useState([0, 100]); // Global risk filter
    const [comingSoon, setComingSoon] = useState(null);
    const sortRef = useRef(null);

    const showSoon = (feat) => {
        setComingSoon(`${feat} is coming soon!`);
        setTimeout(() => setComingSoon(null), 3000);
    };

    // Save favorites to localStorage
    useEffect(() => {
        localStorage.setItem('case_favorites', JSON.stringify(favorites));
    }, [favorites]);

    const toggleFavorite = (e, id) => {
        e.stopPropagation();
        setFavorites(prev => 
            prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
        );
    };

    const [dragging, setDragging] = useState(null); // null, 0 (min), or 1 (max)
    const riskTrackRef = useRef(null);

    const handleRiskClick = (e) => {
        if (!riskTrackRef.current) return;
        const rect = riskTrackRef.current.getBoundingClientRect();
        const p = Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
        
        // Snap nearest handle
        const distMin = Math.abs(p - riskRange[0]);
        const distMax = Math.abs(p - riskRange[1]);
        if (distMin < distMax) setRiskRange([p, riskRange[1]]);
        else setRiskRange([riskRange[0], p]);
    };

    useEffect(() => {
        const handleGlobalMove = (e) => {
            if (dragging === null || !riskTrackRef.current) return;
            const rect = riskTrackRef.current.getBoundingClientRect();
            const p = Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
            
            setRiskRange(prev => {
                const next = [...prev];
                if (dragging === 0) next[0] = Math.min(p, next[1] - 5);
                else next[1] = Math.max(p, next[0] + 5);
                return next;
            });
        };
        const handleGlobalUp = () => setDragging(null);

        if (dragging !== null) {
            window.addEventListener('mousemove', handleGlobalMove);
            window.addEventListener('mouseup', handleGlobalUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleGlobalMove);
            window.removeEventListener('mouseup', handleGlobalUp);
        };
    }, [dragging]);

    const filteredAndSortedCases = useMemo(() => {
        let result = MOCK_CASES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
        
        if (activeTab === 'favorites') {
            result = result.filter(c => favorites.includes(c.id));
        }

        // Global risk filter logic
        result = result.filter(c => {
            const totalChance = (c.items || []).reduce((acc, i) => acc + i.chance, 0);
            const winChance = (c.items || []).filter(i => i.price >= c.price).reduce((acc, i) => acc + (i.chance / totalChance), 0);
            const risk = c.manualRisk !== undefined ? c.manualRisk : (1 - winChance) * 100;
            return risk >= riskRange[0] && risk <= riskRange[1];
        });

        if (sortOrder === 'high') return result.sort((a, b) => b.price - a.price);
        if (sortOrder === 'low') return result.sort((a, b) => a.price - b.price);
        if (sortOrder === 'newest') return result.sort((a, b) => b.id - a.id);
        if (sortOrder === 'oldest') return result.sort((a, b) => a.id - b.id);
        return result;
    }, [search, sortOrder, activeTab, favorites, riskRange]);

    if (selectedCase) {
        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', color: 'white' }}>
                <CaseDetail 
                    caseData={selectedCase} 
                    onBack={() => setSelectedCase(null)} 
                    favorites={favorites}
                    onToggleFavorite={(id) => setFavorites(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id])}
                />
            </div>
        );
    }

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', color: 'white', padding: '10px 0 150px 0', fontFamily: "'Montserrat', sans-serif" }}>
            {/* TOP TITLE */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '30px' }}>
                <FaBoxOpen style={{ fontSize: '24px', color: THEME_COLOR }} />
                <h2 style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>Cases Unboxing</h2>
            </div>

            {/* CREATE BANNER */}
            <div style={{ 
                background: 'rgba(255,255,255,0.03)', 
                border: '1px solid rgba(255,255,255,0.05)', 
                borderRadius: '16px', 
                padding: '20px 30px', 
                marginBottom: '30px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                maxWidth: '1200px',
                width: '100%',
                margin: '0 auto 30px auto'
            }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '900', color: 'white', textTransform: 'uppercase' }}>Create your custom cases</span>
                        <span style={{ background: THEME_COLOR, fontSize: '10px', fontWeight: '900', padding: '2px 8px', borderRadius: '6px' }}>NEW</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontWeight: '600' }}>
                        You can <span style={{ color: THEME_COLOR }}>create cases</span> with items and odds of your choice. <span style={{ color: THEME_COLOR }}>Earn up to 2%</span> of your cases price each time a user unboxes it.
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button onClick={() => showSoon('Your Cases Archive')} style={{ background: 'rgba(255,255,255,0.03)', border: 'none', padding: '10px 20px', borderRadius: '10px', color: 'rgba(255,255,255,0.3)', fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>YOUR CASES</button>
                    <button onClick={() => {
                        try { new Audio('/error.mp3').play().catch(e => console.log(e)); } catch(e){}
                        if (window._addToast) window._addToast({ type: 'error', title: 'COMING SOON', message: 'Case Creation will be available in a future update!' });
                    }} style={{ background: THEME_COLOR, border: 'none', padding: '10px 20px', borderRadius: '10px', color: 'white', fontSize: '12px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>CREATE CASE <FaBolt /></button>
                </div>
            </div>

            {/* SEARCH & SORT ROW */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', maxWidth: '1200px', width: '100%', margin: '0 auto 20px auto' }}>
                <div style={{ position: 'relative', width: '350px' }}>
                    <input 
                        type="text" 
                        value={search} 
                        onChange={(e) => setSearch(e.target.value)} 
                        placeholder="Search for cases" 
                        style={{ width: '100%', background: '#1c1c26', border: '1px solid rgba(255,255,255,0.05)', padding: '12px 15px 12px 40px', borderRadius: '8px', color: 'white', fontSize: '14px', outline: 'none' }} 
                    />
                    <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', fontSize: '14px' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', fontWeight: '600' }}>Sort by:</span>
                        <div style={{ position: 'relative' }} ref={sortRef}>
                            <button 
                                onClick={() => setShowSort(!showSort)}
                                style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.05)', padding: '10px 15px', borderRadius: '8px', color: 'white', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                            >
                                {sortOrder === 'high' ? 'Price Descending' : sortOrder === 'low' ? 'Price Ascending' : sortOrder === 'newest' ? 'Newest' : 'Oldest'} <FaChevronDown size={10} style={{ transform: showSort ? 'rotate(180deg)' : 'none', transition: '0.2s', color: 'rgba(255,255,255,0.3)' }} />
                            </button>
                            
                            <AnimatePresence>
                                {showSort && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        style={{ position: 'absolute', top: 'calc(100% + 5px)', right: 0, width: '180px', background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '5px', zIndex: 1000, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
                                    >
                                        {[
                                            { label: 'Price Descending', val: 'high' },
                                            { label: 'Price Ascending', val: 'low' },
                                            { label: 'Newest', val: 'newest' },
                                            { label: 'Oldest', val: 'oldest' }
                                        ].map(opt => (
                                            <div 
                                                key={opt.val}
                                                onClick={() => { setSortOrder(opt.val); setShowSort(false); }}
                                                style={{ padding: '10px 15px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: sortOrder === opt.val ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: sortOrder === opt.val ? 'rgba(255,255,255,0.05)' : 'transparent' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                                onMouseLeave={e => e.currentTarget.style.background = sortOrder === opt.val ? 'rgba(255,255,255,0.05)' : 'transparent'}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>

            {/* CATEGORIES ROW */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '35px', maxWidth: '1200px', width: '100%', margin: '0 auto 35px auto', overflowX: 'auto', paddingBottom: '10px' }}>
                <button 
                    onClick={() => { setActiveTab('original'); setRiskRange([0, 100]); }}
                    style={{ background: activeTab === 'original' ? THEME_COLOR : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === 'original' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    <FaTrophy style={{ opacity: activeTab === 'original' ? 1 : 0.5 }} /> Official
                </button>
                <button 
                    onClick={() => { setActiveTab('1%'); setRiskRange([90, 100]); }}
                    style={{ background: activeTab === '1%' ? THEME_COLOR : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === '1%' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    1%
                </button>
                <button 
                    onClick={() => { setActiveTab('5%'); setRiskRange([50, 90]); }}
                    style={{ background: activeTab === '5%' ? THEME_COLOR : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === '5%' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    5%
                </button>
                <button 
                    onClick={() => { setActiveTab('10%'); setRiskRange([0, 50]); }}
                    style={{ background: activeTab === '10%' ? THEME_COLOR : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === '10%' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    10%
                </button>

                <button 
                    onClick={() => { setActiveTab('favorites'); setRiskRange([0, 100]); }}
                    style={{ background: activeTab === 'favorites' ? THEME_COLOR : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === 'favorites' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    <FaHeart style={{ opacity: activeTab === 'favorites' ? 1 : 0.5 }} /> Favorites
                </button>
            </div>

            {/* CASE LIST */}
            <div style={{ maxWidth: '1250px', margin: '0 auto', width: '100%' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
                    {filteredAndSortedCases.length === 0 ? (
                        <div style={{ gridColumn: 'span 5', textAlign: 'center', padding: '100px', color: 'rgba(255,255,255,0.1)', fontSize: '14px', fontWeight: '900', textTransform: 'uppercase' }}>No cases found</div>
                    ) : filteredAndSortedCases.map(box => {
                        const totalChance = (box.items || []).reduce((acc, item) => acc + item.chance, 0);
                        const winChance = (box.items || []).filter(i => i.price >= box.price).reduce((acc, item) => acc + (item.chance / totalChance), 0);
                        const risk = box.manualRisk !== undefined ? box.manualRisk : Math.max(5, Math.min(95, (1 - winChance) * 100));
                        const caseColor = box.themeColor || box.items[0]?.color || (risk > 75 ? '#ff3131' : (risk > 50 ? '#f59e0b' : (risk > 25 ? '#a855f7' : '#22c55e')));

                        return (
                            <div 
                                key={box.id} 
                                onClick={() => setSelectedCase(box)} 
                                className="rc-caseCard"
                                style={{ 
                                    background: '#121217', 
                                    border: '1px solid rgba(255,255,255,0.03)', 
                                    borderRadius: '8px', 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    height: '260px'
                                }}
                            >
                                {/* Hover Preview Icon */}
                                <div 
                                    className="rc-casePreviewIcon" 
                                    style={{ position: 'absolute', top: '10px', right: '10px', width: '32px', height: '32px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', zIndex: 10, transition: '0.2s', opacity: 0 }}
                                    onClick={(e) => { e.stopPropagation(); setPreviewCase(box); }}
                                >
                                    <FaSearch size={14} />
                                </div>

                                <div style={{ padding: '15px 10px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', position: 'relative', flex: 1 }}>
                                    <div 
                                        onClick={(e) => toggleFavorite(e, box.id)}
                                        style={{ position: 'absolute', top: '10px', left: '10px', color: favorites.includes(box.id) ? '#ff3131' : 'rgba(255,255,255,0.1)', transition: '0.2s', zIndex: 5 }}
                                    >
                                        <FaHeart size={14} style={{ 
                                            filter: favorites.includes(box.id) ? 'drop-shadow(0 0 10px rgba(255,49,49,0.8))' : 'none',
                                            cursor: 'pointer'
                                        }} />
                                    </div>

                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                        <img src={box.img} alt={box.name} style={{ width: '100px', height: '100px', objectFit: 'contain', filter: `drop-shadow(0 5px 15px ${caseColor}44)` }} />
                                    </div>
                                    
                                    <div style={{ fontSize: '14px', fontWeight: '800', color: 'white', marginBottom: '12px', textTransform: 'capitalize', letterSpacing: '0px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center' }}>{box.name}</div>

                                    <div style={{ background: '#1c1c26', width: 'fit-content', padding: '8px 16px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                                        <ScrapIcon size={13} color="#f59e0b" />
                                        <span style={{ fontSize: '14px', fontWeight: '800', color: 'white' }}>{box.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                    </div>
                                </div>
                                
                                {/* Segmented Risk Bar */}
                                <div style={{ width: '100%', padding: '0 12px 12px', position: 'relative' }}>
                                    <div style={{ position: 'relative', height: '4px', width: '100%', display: 'flex', gap: '2px' }}>
                                        <div style={{ flex: 1, background: '#22c55e', borderRadius: '2px 0 0 2px' }} />
                                        <div style={{ flex: 1, background: '#84cc16' }} />
                                        <div style={{ flex: 1, background: '#f59e0b' }} />
                                        <div style={{ flex: 1, background: '#ef4444', borderRadius: '0 2px 2px 0' }} />
                                        
                                        {/* Marker Arrow */}
                                        <div style={{ 
                                            position: 'absolute', 
                                            bottom: 'calc(100% + 4px)', 
                                            left: `${risk}%`, 
                                            transform: 'translateX(-50%)',
                                            width: 0,
                                            height: 0,
                                            borderLeft: '4px solid transparent',
                                            borderRight: '4px solid transparent',
                                            borderTop: '5px solid #fff',
                                            transition: 'left 0.5s ease-out'
                                        }} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <AnimatePresence>
                {previewCase && (
                    <CasePreviewModal 
                        caseData={previewCase} 
                        onClose={() => setPreviewCase(null)} 
                        onGoToCase={() => { setSelectedCase(previewCase); setPreviewCase(null); }} 
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {comingSoon && <ComingSoonToast message={comingSoon} />}
            </AnimatePresence>
        </div>
    );
};

export { MOCK_CASES };
export default Cases;

